import { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Download, Share2, Copy, Check, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { drawShareCard, FORMATS } from "@/lib/shareCard";

/**
 * "I'm running LATENT 5K" share card — renders a branded PNG on a canvas
 * and hands it to the phone's share sheet (Instagram / WhatsApp) or downloads it.
 */
export default function ShareEventCard({ event, children }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [city, setCity] = useState("");
  const [format, setFormat] = useState("post");
  const [copied, setCopied] = useState(false);
  const canvasRef = useRef(null);

  useEffect(() => {
    if (open) {
      setName(user?.name || "");
      setCity(user?.city || "");
    }
  }, [open, user]);

  useEffect(() => {
    if (!open || !canvasRef.current) return;
    drawShareCard(canvasRef.current, { event, name, city, format, siteUrl: window.location.host });
  }, [open, event, name, city, format]);

  if (!event?.enabled || !event?.title) return null;

  const fileName = `im-running-${event.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}-${format}.png`;
  const caption = buildCaption(event);

  const toBlob = () => new Promise((res) => canvasRef.current.toBlob(res, "image/png"));

  const share = async () => {
    try {
      const blob = await toBlob();
      const file = new File([blob], fileName, { type: "image/png" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], text: caption });
        return;
      }
      await download();
      toast.message("Image downloaded — post it on Instagram or WhatsApp");
    } catch (e) {
      if (e?.name !== "AbortError") toast.error("Could not open share sheet — try Download");
    }
  };

  const download = async () => {
    const blob = await toBlob();
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(a.href);
  };

  const copyCaption = async () => {
    try {
      await navigator.clipboard.writeText(caption);
      setCopied(true);
      toast.success("Caption copied");
      setTimeout(() => setCopied(false), 1800);
    } catch { toast.error("Copy failed"); }
  };

  const f = FORMATS[format];

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent className="max-w-3xl bg-zinc-950 border-zinc-800 text-white rounded-none p-0 overflow-hidden" data-testid="share-card-dialog">
        <div className="grid grid-cols-1 md:grid-cols-5">
          {/* Preview */}
          <div className="md:col-span-3 bg-zinc-900 p-5 flex items-center justify-center">
            <canvas
              ref={canvasRef}
              width={f.w}
              height={f.h}
              data-testid="share-card-canvas"
              className={`hairline border-zinc-800 shadow-2xl ${format === "story" ? "max-h-[70vh]" : "max-h-[70vh]"} w-auto h-auto max-w-full`}
              style={{ aspectRatio: `${f.w} / ${f.h}` }}
            />
          </div>

          {/* Controls */}
          <div className="md:col-span-2 p-5 space-y-4">
            <DialogHeader>
              <p className="font-mono text-[10px] tracking-[0.3em] text-orange-500 uppercase">SPREAD THE WORD</p>
              <DialogTitle className="font-display text-2xl text-white tracking-wider">I&apos;M RUNNING {event.title.toUpperCase()}</DialogTitle>
            </DialogHeader>

            <div>
              <Label className="font-display tracking-widest text-xs text-zinc-400">YOUR NAME</Label>
              <Input data-testid="share-card-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name"
                className="mt-2 rounded-none bg-zinc-950 border-zinc-800 text-white focus-visible:ring-orange-600" />
            </div>
            <div>
              <Label className="font-display tracking-widest text-xs text-zinc-400">CITY (OPTIONAL)</Label>
              <Input data-testid="share-card-city" value={city} onChange={(e) => setCity(e.target.value)} placeholder="Rourkela"
                className="mt-2 rounded-none bg-zinc-950 border-zinc-800 text-white focus-visible:ring-orange-600" />
            </div>

            <div>
              <Label className="font-display tracking-widest text-xs text-zinc-400">FORMAT</Label>
              <div className="mt-2 grid grid-cols-2 gap-2">
                {Object.entries(FORMATS).map(([k, v]) => (
                  <button
                    key={k}
                    type="button"
                    data-testid={`share-card-format-${k}`}
                    onClick={() => setFormat(k)}
                    className={`hairline px-3 py-2 font-display text-xs tracking-[0.2em] transition ${
                      format === k ? "bg-orange-600 border-orange-500 text-white" : "border-zinc-700 text-zinc-400 hover:text-white"
                    }`}
                  >
                    {v.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-2 pt-2">
              <Button data-testid="share-card-share-btn" onClick={share} className="w-full rounded-none bg-orange-600 hover:bg-orange-500 font-display tracking-wider py-5">
                <Share2 size={16} className="mr-2" /> SHARE TO INSTAGRAM / WHATSAPP
              </Button>
              <div className="grid grid-cols-2 gap-2">
                <Button data-testid="share-card-download-btn" onClick={download} variant="outline" className="rounded-none border-zinc-700 bg-transparent text-white hover:bg-zinc-900 font-display tracking-wider text-xs">
                  <Download size={14} className="mr-1.5" /> DOWNLOAD
                </Button>
                <Button data-testid="share-card-copy-btn" onClick={copyCaption} variant="outline" className="rounded-none border-zinc-700 bg-transparent text-white hover:bg-zinc-900 font-display tracking-wider text-xs">
                  {copied ? <Check size={14} className="mr-1.5 text-orange-500" /> : <Copy size={14} className="mr-1.5" />} CAPTION
                </Button>
              </div>
              <a
                href={`https://wa.me/?text=${encodeURIComponent(caption)}`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="share-card-whatsapp-link"
                className="flex items-center justify-center gap-2 w-full hairline border-zinc-800 bg-zinc-900 hover:border-orange-600/60 px-3 py-2.5 font-mono text-[10px] tracking-[0.25em] uppercase text-zinc-300 transition"
              >
                <MessageCircle size={13} className="text-orange-500" /> SEND TEXT INVITE ON WHATSAPP
              </a>
            </div>
            <p className="font-mono text-[9px] text-zinc-500 tracking-widest uppercase leading-relaxed">
              On phones, Share opens your apps directly. On desktop, download and post from your phone.
            </p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function buildCaption(event) {
  const when = event.date ? new Date(`${event.date}T05:30:00+05:30`).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" }) : "soon";
  const where = event.location ? ` at ${event.location}` : "";
  const link = `${window.location.origin}/`;
  const tag = event.title.replace(/[^a-z0-9]/gi, "").toUpperCase();
  return `I'm running ${event.title} with Odisha Steel City Runners on ${when}${where}. Come run with me! Register: ${link} #OSCR #${tag} #OdishaRuns`;
}
